import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Button, Tooltip, Snackbar, Alert } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SectionTabs from "./SectionTabs";
import sectionDescriptions from "../sectionDescriptions.json";
import SectionDetails from "./SectionDetails";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CreateMilestoneModal from "./CreateMilestoneModal";
import { clearAllWafrQuestionnaireMessages } from "../../../store/wafrQuestionnaire/wafrQuestionnaireSlice";
import { isAuditor } from "../../../helpers/jwt-token-access/isAuditor";
import TimelineIcon from "@mui/icons-material/Timeline";
import DefaultAWSIcon from "../../../assets/images/compliance/aws.svg";
import cloudcatcherIcon from "../../../assets/images/Collapsed_BlueOrange_Curve.png";

const WorkloadAssessment = ({
  wlaData,
  orgCode,
  accCode,
  accNumber,
  assessmentName,
  frameworkName,
  ownerName,
  approverName,
  isReadOnly = false,
  isMilestoneView = false,
  workloadId,
  handleFilterChange,
  status,
  initialData,
  is_ta_enabled,
}) => {
  console.log("🚀 ~ workloadId:", workloadId);
  const history = useHistory();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(() => {
    const savedTab = sessionStorage.getItem(`workload-assessment-tab-${workloadId}`);
    return savedTab || "Operational Excellence";
  });
  
  const [selectedQuestion, setSelectedQuestion] = useState(() => {
    const savedQuestion = sessionStorage.getItem(`workload-assessment-question-${workloadId}`);
    return savedQuestion !== null ? parseInt(savedQuestion) : 0;
  });
  
  const hasRestoredState = useRef(false);
  
  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedTab) {
      sessionStorage.setItem(`workload-assessment-tab-${workloadId}`, selectedTab);
    }
  }, [selectedTab, workloadId]);

  useEffect(() => {
    if (selectedQuestion !== null) {
      sessionStorage.setItem(`workload-assessment-question-${workloadId}`, selectedQuestion.toString());
    }
  }, [selectedQuestion, workloadId]);
  const [openMilestoneModal, setOpenMilestoneModal] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { createMilestoneMessage, createMilestoneError } = useSelector(
    (state) => state.wafrQuestionnaire
  );
  console.log("🚀 ~ createMilestoneError:", createMilestoneError);
  console.log("🚀 ~ createMilestoneMessage:", createMilestoneMessage);
  const dispatch = useDispatch();

  // Snackbar close handler
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (createMilestoneMessage) {
      setSnackbar({
        open: true,
        message: createMilestoneMessage,
        severity: "success",
      });
      setOpenMilestoneModal(false);
    }
    if (createMilestoneError) {
      setSnackbar({
        open: true,
        message: createMilestoneError,
        severity: "error",
      });
    }

    if (createMilestoneMessage || createMilestoneError) {
      dispatch(clearAllWafrQuestionnaireMessages());
    }
  }, [createMilestoneMessage, createMilestoneError, dispatch]);

  // Clean up sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if we're navigating away from this workload
      const currentWorkloadId = sessionStorage.getItem('current-workload-id');
      if (currentWorkloadId !== workloadId) {
        sessionStorage.removeItem(`workload-assessment-tab-${workloadId}`);
        sessionStorage.removeItem(`workload-assessment-question-${workloadId}`);
      }
    };
  }, [workloadId]);

  // Set current workload ID and reset state restoration flag
  useEffect(() => {
    sessionStorage.setItem('current-workload-id', workloadId);
    hasRestoredState.current = false;
  }, [workloadId]);

  // Validate and restore state when data is available
  useEffect(() => {
    if (wlaData && !hasRestoredState.current) {
      const tabSections = Object.keys(wlaData);
      
      // Validate that the current tab exists in the available sections
      if (!tabSections.includes(selectedTab)) {
        setSelectedTab("Operational Excellence");
      }
      
      // Validate that the current question index is within bounds
      const currentSectionQuestions = wlaData[selectedTab] || [];
      if (selectedQuestion >= currentSectionQuestions.length) {
        setSelectedQuestion(0);
      }
      
      hasRestoredState.current = true;
    }
  }, [wlaData, selectedTab, selectedQuestion]);

  if (!wlaData) {
    return null;
  }

  const tabSections = Object.keys(wlaData);

  const sectionInfo = sectionDescriptions[selectedTab];
  const sectionQuestionDetailList = wlaData[selectedTab];
  const sectionQuestionList = sectionQuestionDetailList?.map((que) => ({
    questionTitle: que.questionTitle,
    risk: que.risk,
    reason: que.reason,
  }));
  const sectionQuestionLabel = sectionInfo?.label;

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedQuestion(0);
  };

  const handleMilestonesClick = () => {
    // Prefer prop; fall back to location.state so return from milestone detail
    // does not push /milestones without workloadId.
    const locationState = location.state || {};
    const resolvedWorkloadId = workloadId || locationState.workloadId;

    history.push(`/workload-assessment/milestones`, {
      workloadId: resolvedWorkloadId,
      orgCode: orgCode || locationState.orgCode,
      accCode: accCode || locationState.accCode,
      accNumber: accNumber || locationState.accNumber,
      assessmentName: locationState.assessmentName || assessmentName,
      frameworkName: frameworkName || locationState.frameworkName,
      ownerName: ownerName || locationState.ownerName,
      approverName: approverName || locationState.approverName,
      status: status || locationState.status,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={2}
        sx={{
          padding: "0.8rem 1.2rem",
          borderRadius: "8px",
          marginBottom: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#f9fafe",
        }}
      >
        {/* Left Section: All Details in One Line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1.5rem",
            fontSize: "0.875rem",
          }}
        >
          {/* Logo based on assessment type */}
          {is_ta_enabled !== undefined && (
            <Tooltip 
              title={is_ta_enabled ? "AWS Recommended" : "CloudCatcher Automation"} 
              arrow 
              placement="top"
            >
              <Box
                component="img"
                src={is_ta_enabled ? DefaultAWSIcon : cloudcatcherIcon}
                alt={is_ta_enabled ? "AWS Recommended" : "CloudCatcher"}
                sx={{
                  width: 24,
                  height: 24,
                  objectFit: "contain",
                }}
              />
            </Tooltip>
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "600",
              color: "#2a3c50",
              whiteSpace: "nowrap",
            }}
          >
            {assessmentName || "Workload Name N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "400",
              color: "#6b7280",
            }}
          >
            | Account Number: {accNumber || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "400",
              color: "#6b7280",
            }}
          >
            | Framework Name: {frameworkName || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "400",
              color: "#6b7280",
            }}
          >
            | Assessment Owner: {ownerName || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "400",
              color: "#6b7280",
            }}
          >
            | Approver Name: {approverName || "N/A"}
          </Typography>
        </Box>

        {/* Right Section: Back Button and Create Milestone */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={() => history.goBack()}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "#e3f2fd",
              },
            }}
          >
            Back
          </Button>

          {/* Milestones Button — hidden when already viewing a selected milestone */}
          {!isMilestoneView && (
            <Button
              variant="outlined"
              onClick={handleMilestonesClick}
              startIcon={<TimelineIcon sx={{ color: "#0288d1" }} />}
              sx={{
                textTransform: "none",
                color: "#0288d1",
                fontSize: "14px",
                borderColor: "#0288d1",
                "&:hover": {
                  borderColor: "#0288d1",
                  backgroundColor: "rgba(2, 136, 209, 0.04)",
                },
              }}
            >
              Milestones
            </Button>
          )}

          {/* Create Milestone Button */}
          {!isReadOnly && (
            <Tooltip
              title={
                !isAuditor()
                  ? "Only Auditors have access to this feature"
                  : status === "COMPLETED"
                  ? "Cannot create milestone for completed assessment"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => setOpenMilestoneModal(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: "500",
                  }}
                  disabled={!isAuditor() || status === "COMPLETED"}
                >
                  Create Milestone
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      <Paper
        sx={{
          backgroundColor: "white",
          borderRadius: "0.5rem",
          padding: "0.5rem",
        }}
      >
        <SectionTabs
          sections={tabSections}
          selectedTab={selectedTab}
          handleTabChange={handleTabChange}
          questionnaires={initialData}
          handleFilterChange={handleFilterChange}
        />
      </Paper>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <SectionDetails
          assessmentName={assessmentName}
          sectionQuestionLabel={sectionQuestionLabel}
          sectionQuestionList={sectionQuestionList}
          sectionQuestionDetailList={sectionQuestionDetailList}
          selectedQuestion={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          orgCode={orgCode}
          accCode={accCode}
          accNumber={accNumber}
          isReadOnly={isReadOnly}
        />
      </Box>

      {/* Add Modal */}
      <CreateMilestoneModal
        open={openMilestoneModal}
        onClose={() => setOpenMilestoneModal(false)}
        workloadId={workloadId}
        accCode={accCode}
        orgCode={orgCode}
      />

      {/* Snackbar for milestone messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkloadAssessment;
